<template>
  <div
    class="tw-rounded-2xl tw-bg-white dark:tw-bg-slate-800 tw-border tw-border-slate-100 dark:tw-border-slate-700 tw-shadow tw-py-3.5 tw-flex tw-flex-col tw-gap-3"
  >
    <div
      class="tw-text-sm tw-text-slate-700 dark:tw-text-slate-50 tw-font-body tw-font-medium tw-px-4"
    >
      <div
        class="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-items-center tw-gap-3"
      >
        <div
          class="tw-text-sm tw-text-slate-700 dark:tw-text-slate-50 tw-font-body tw-font-medium"
        >
          Stats
        </div>
        <div class="tw-flex tw-items-center tw-gap-2">
          <Button
            icon="pi pi-sync tw-text-slate-700 dark:tw-text-slate-50 tw-text-sm"
            class="tw-h-7 tw-shadow tw-w-8 tw-bg-white dark:tw-bg-slate-800 tw-border-transparent dark:tw-border-slate-700 tw-text-sm tw-font-medium tw-font-body"
            @click="refreshData()"
          />
          <download-csv :data="formattedData">
            <Button
              label="CSV"
              icon="pi pi-download tw-mr-1.5 tw-text-sm"
              class="tw-h-7 tw-shadow tw-w-[68px] tw-px-2 tw-bg-white dark:tw-bg-slate-800 tw-border-transparent dark:tw-border-slate-700 tw-text-slate-700 dark:tw-text-slate-50 tw-text-sm tw-font-medium tw-font-body"
              :disabled="isTableEmpty"
            ></Button>
          </download-csv>
        </div>
      </div>
    </div>
    <div>
      <DataTable
        :value="formattedData"
        :paginator="true"
        :rows="10"
        responsiveLayout="scroll"
        :loading="props.loading"
        class="tw-bg-none tw-bg-transparent podcaster-stats-table"
        table-class="tw-bg-none tw-bg-transparent"
        :row-class="() => 'tw-bg-none tw-bg-transparent'"
      >
        <Column field="date" header="Date" />
        <Column field="downloads" header="Downloads" />
        <Column field="impressions" header="Impressions" />
        <Column field="imp_download_ratio" header="Imp/Download Ratio" />
        <Column field="revenue" header="Gross Profit" />
        <Column field="rev_download_ratio" header="Rev/Download Ratio" />
        <Column field="profit_margin" header="Profit Margin" />
        <Column field="ecpm" header="ECPM" />

        <template #empty>
          <div
            class="tw-flex tw-items-center tw-justify-center tw-min-h-[349px] tw-w-full tw-text-sm tw-text-slate-500 dark:tw-text-slate-300 tw-font-medium tw-font-body"
          >
            <LoaderSm v-if="props.loading" />
            <span v-else>Show data populating...</span>
          </div>
        </template>
      </DataTable>
    </div>
  </div>
</template>

<script setup>
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import Button from "primevue/button";
import LoaderSm from "@/components/LoaderSm.vue";
import { defineProps, defineEmits, computed } from "vue";

const props = defineProps(["loading", "data"]);

const emit = defineEmits(["refreshData"]);

const formatStatsData = (data = []) =>
  data.map((item) => {
    const revenue = Number(item.revenue ?? 0);
    const ecpm = Number(item.ecpm ?? 0);
    const profitMargin = Number(item.profit_margin ?? 0);
    const impDownloadRatio = Number(item.imp_download_ratio ?? 0);
    const revDownloadRatio = Number(item.rev_download_ratio ?? 0);

    return {
      ...item,
      revenue: `$${Number.isFinite(revenue) ? revenue.toFixed(2) : "0.00"}`,
      ecpm: `${Number.isFinite(ecpm) ? ecpm.toFixed(4) : "0"}`,
      profit_margin: `${
        Number.isFinite(profitMargin) ? (profitMargin * 100).toFixed(2) : "0"
      }%`,
      imp_download_ratio: `${
        Number.isFinite(impDownloadRatio) ? impDownloadRatio.toFixed(3) : "0"
      }`,
      rev_download_ratio: `${
        Number.isFinite(revDownloadRatio) ? revDownloadRatio.toFixed(3) : "0"
      }`,
    };
  });

const formattedData = computed(() => formatStatsData(props.data || []));

const isTableEmpty = computed(
  () => !Array.isArray(props.data) || props.data.length === 0
);

const refreshData = () => {
  // Implement refresh functionality here
  emit("refreshData");
};
</script>

<style>
.podcaster-stats-table .p-paginator-bottom {
  border-color: transparent !important;
}
.podcaster-stats-table .p-datatable-thead th {
  background-color: transparent !important;
}
.podcaster-stats-table .p-paginator {
  background-color: transparent !important;
  border-color: transparent !important;
}
.podcaster-stats-table td,
.podcaster-stats-table th {
  border-color: #f1f5f9 !important; /* tw-border-slate-100 */
}
.tw-dark .podcaster-stats-table td,
.tw-dark .podcaster-stats-table th {
  border-color: #334155 !important; /* dark:tw-border-slate-700 */
}
.podcaster-stats-table .p-datatable,
.podcaster-stats-table .p-datatable-wrapper,
.podcaster-stats-table .p-datatable-tbody > tr,
.podcaster-stats-table .p-datatable-tbody > tr > td {
  background-color: transparent !important;
}
.podcaster-stats-table .p-datatable-wrapper {
  min-height: 349px;
}
</style>
